import React, { useState, useRef, useEffect } from 'react';
import { Settings, Move, Target, RefreshCw, ArrowUp, ArrowDown, ChevronUp, ChevronDown, MousePointer2, Anchor, RotateCw, ArrowLeft, ArrowRight, Activity, MoveHorizontal, Ruler, Crosshair } from 'lucide-react';

const ManualToolControl = ({
  isConnected,
  sendCommand,
  motorConfig,
  setMotorConfig,
  setManualOffset,
  ejecutando,
  pausado,
  clavosRestantes,
  setClavosRestantes,
  modoClavado,
  onDisparoManual,
  encoderLiveValue,
  encoderHistory = [],
}) => {
  const [activeTab, setActiveTab] = useState('hilador');
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Estados de calibración del motor auxiliar (pulsos por cm)
  const [calibPulsosInput, setCalibPulsosInput] = useState(1000);
  const [calibPulsosAcumulados, setCalibPulsosAcumulados] = useState(0);
  const [calibCmMedidos, setCalibCmMedidos] = useState('');
  const [calibResultado, setCalibResultado] = useState(null);
  // Posición estimada del auxiliar en cm (relativa al último cero)
  const [posAuxiliarCm, setPosAuxiliarCm] = useState(0);
  
  // Defaults seguros desde motorConfig
  const {
    velocidadHilador = 2000,
    aceleracionHilador = 10000,
    velocidadElevador = 400,
    aceleracionElevador = 800,
    velocidadPrincipalManual = 400,
    aceleracionPrincipalManual = 800,
    tiempoDisparo = 150,
    anguloDentro = 0,
    anguloFuera = 0,
    inputElevadorManual = 10,
    inputPrincipalManual = 10,
    invertirRele = false,
    invertirPrincipal = false,
    invertirHilador = false,
    invertirElevador = false,
    invertirAuxiliar = false,
    velocidadAuxiliar = 1200,
    aceleracionAuxiliar = 15000,
    pulsosPorCmAuxiliar = 100,
    inputAuxiliarManual = 1,
  } = motorConfig || {};
  
  // Referencias para el long press
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Referencia para tener siempre el motorConfig actualizado en los intervalos
  const motorConfigRef = useRef(motorConfig);
  motorConfigRef.current = motorConfig; // Actualización inmediata en cada render

  // Estados Disparador ya no usa estado local, usa props

  // Constantes
  const PASOS_POR_GRADO = 1;

  // Función genérica para manejar start de pulsación con aceleración progresiva
  const handlePressStart = (actionCallback) => {
    if (!isConnected || (ejecutando && !pausado)) return; // Bloqueo si está ejecutando y NO pausado
    
    // Ejecutar inmediatamente la primera vez (velocidad x1)
    actionCallback(1);
    
    // Limpiar temporizadores anteriores por seguridad
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    const startTime = Date.now();

    // Iniciar repetición después de un delay inicial (500ms)
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        let speedMultiplier = 1;

        // Aceleración progresiva basada en tiempo presionado
        if (elapsed > 2000) {
           speedMultiplier = 5; // x5 después de 2 segundos (muy rápido)
        } else if (elapsed > 1000) {
           speedMultiplier = 2; // x2 después de 1 segundo
        }

        actionCallback(speedMultiplier);
      }, 100); // Repetir cada 100ms
    }, 500);
  };

  const handlePressEnd = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  useEffect(() => {
    return () => handlePressEnd();
  }, []);



  // Handlers
  const handleMoverHiladorRelativo = (deltaGrados, campo) => {
    if (!isConnected) return;
    
    // 1. Calculamos el nuevo ángulo calibrado usando la referencia para evitar cierres obsoletos
    const valorActual = motorConfigRef.current?.[campo] || 0;
    const nuevoAngulo = valorActual + deltaGrados;

    // 2. Actualizamos la calibración global en la App
    if (setMotorConfig) {
      setMotorConfig(prev => ({
        ...prev,
        [campo]: nuevoAngulo
      }));
    }

    // 3. Movemos el motor para feedback visual (Usamos ángulo absoluto para que el ESP32 use el vector corto)
    sendCommand('MOTOR_HILADOR_ANGULO', { 
      angulo: nuevoAngulo,
      velocidad: velocidadHilador,
      aceleracion: aceleracionHilador
    });
  };

  const handleMoverElevador = (distanciaMm) => {
    if (!isConnected) return;
    
    const factor = motorConfig.pasosPorMmElevador || 1;
    const pasosCalculados = Math.round(parseFloat(distanciaMm) * factor);

    sendCommand('ELEVADOR_MOVER_RELATIVO', { 
      pasos: pasosCalculados,
      velocidad: velocidadElevador,
      aceleracion: aceleracionElevador
    });
  };

  const handleMoverPrincipal = (pasos) => {
    if (!isConnected) return;
    
    // Actualizamos el desfase acumulado
    if (setManualOffset) {
        setManualOffset(prev => prev + parseInt(pasos));
    }

    const velSegura = velocidadPrincipalManual || 400;
    const acelSegura = aceleracionPrincipalManual || 800;

    console.log("Moviendo principal manual:", { pasos, vel: velSegura, acel: acelSegura });

    sendCommand('MOTOR_PRINCIPAL_MOVER', { 
      pasos: parseInt(pasos),
      velocidad: velSegura,
      aceleracion: acelSegura
    });
  };

  // ---------- Motor Auxiliar (posicionador de la pistola en el eje X) ----------

  // Mueve el auxiliar una distancia en cm (admite decimales y negativos)
  const handleMoverAuxiliarCm = (cm) => {
    if (!isConnected) return;
    const distancia = parseFloat(cm);
    if (!distancia || isNaN(distancia)) return;

    const factor = motorConfigRef.current?.pulsosPorCmAuxiliar || pulsosPorCmAuxiliar;
    const pasos = Math.round(distancia * factor);
    if (pasos === 0) return;

    setPosAuxiliarCm(prev => Math.round((prev + distancia) * 1000) / 1000);
    sendCommand('MOTOR_AUXILIAR_MOVER', {
      pasos,
      velocidad: velocidadAuxiliar,
      aceleracion: aceleracionAuxiliar
    });
  };

  // Mueve el auxiliar en pulsos crudos (para calibración) y acumula el desplazamiento
  const handleMoverAuxiliarPulsos = (pasos) => {
    if (!isConnected) return;
    const p = parseInt(pasos);
    if (!p || isNaN(p)) return;

    setCalibPulsosAcumulados(prev => prev + p);
    sendCommand('MOTOR_AUXILIAR_MOVER', {
      pasos: p,
      velocidad: velocidadAuxiliar,
      aceleracion: aceleracionAuxiliar
    });
  };

  const handleAuxiliarSetCero = () => {
    if (!isConnected) return;
    sendCommand('MOTOR_AUXILIAR_SET_CERO');
    setPosAuxiliarCm(0);
  };

  const handleAuxiliarIrACero = () => {
    if (!isConnected) return;
    sendCommand('MOTOR_AUXILIAR_MOVETO', {
      pasos: 0,
      velocidad: velocidadAuxiliar,
      aceleracion: aceleracionAuxiliar
    });
    setPosAuxiliarCm(0);
  };

  // Calcula pulsos/cm con lo movido en calibración y lo guarda en la config
  const handleCalcularCalibracion = () => {
    const cm = parseFloat(calibCmMedidos);
    const pulsos = Math.abs(calibPulsosAcumulados);
    if (!cm || isNaN(cm) || cm <= 0 || pulsos === 0) return;

    const nuevoFactor = Math.round((pulsos / cm) * 100) / 100;
    setCalibResultado(nuevoFactor);
    if (setMotorConfig) {
      setMotorConfig(prev => ({ ...prev, pulsosPorCmAuxiliar: nuevoFactor }));
    }
  };

  const handleReiniciarCalibracion = () => {
    setCalibPulsosAcumulados(0);
    setCalibCmMedidos('');
    setCalibResultado(null);
  };

  const handleInvertirAuxiliar = (checked) => {
    if (setMotorConfig) {
      setMotorConfig(prev => ({ ...prev, invertirAuxiliar: checked }));
    }
    sendCommand('CONFIG_MOTORES', {
      invertirPrincipal: invertirPrincipal || false,
      invertirHilador: invertirHilador || false,
      invertirElevador: invertirElevador || false,
      invertirAuxiliar: checked,
      invertirRele: invertirRele || false
    });
  };

  const handleDisparar = () => {
    if (!isConnected) return;
    if (clavosRestantes > 0) {
      sendCommand('DISPARAR_CLAVO', { tiempo: tiempoDisparo });
      // El decremento de clavosRestantes se maneja en Base.jsx al recibir la confirmación ok del ESP32.
      
      // Si estamos en modo clavado, marcar la posición pero SIN AVANZAR
      if (onDisparoManual) {
        onDisparoManual();
      }
    }
  };

  const handleRecargar = () => {
    setClavosRestantes(90);
  };

  const handleInvertirRele = (checked) => {
    if (setMotorConfig) {
      setMotorConfig(prev => ({ ...prev, invertirRele: checked }));
    }
    sendCommand('CONFIG_MOTORES', {
      invertirPrincipal: invertirPrincipal || false,
      invertirHilador: invertirHilador || false,
      invertirElevador: invertirElevador || false,
      invertirAuxiliar: invertirAuxiliar || false,
      invertirRele: checked
    });
  };

  const renderTabButton = (id, label, icon) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
        activeTab === id
          ? 'border-blue-500 text-blue-400'
          : 'border-transparent text-slate-400 hover:text-slate-300 hover:bg-slate-800/50'
      }`}
    >
      {React.cloneElement(icon, { size: 16 })}
      {label}
    </button>
  );

  const handleInputFocus = (e) => {
    setIsInputFocused(true);
    // Pequeño delay para permitir que el teclado se despliegue antes de calcular el scroll
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const handleUpdateInput = (campo, valor) => {
    if (setMotorConfig) {
      setMotorConfig(prev => ({
        ...prev,
        [campo]: valor
      }));
    }
  };

  return (
    <div className={`bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden h-full flex flex-col relative ${(!isConnected || (ejecutando && !pausado)) ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex border-b border-slate-700/50 overflow-x-auto">
        {renderTabButton('principal', 'Principal', <Anchor />)}
        {renderTabButton('hilador', 'Hilador', <Settings />)}
        {renderTabButton('elevador', 'Elevador', <Move />)}
        {renderTabButton('auxiliar', 'Auxiliar', <MoveHorizontal />)}
        {renderTabButton('disparador', 'Disparador', <Target />)}
        {renderTabButton('encoder', 'Encoder', <Activity />)}
      </div>

      <div className={`p-3 flex-1 overflow-y-auto transition-all duration-300 ${isInputFocused ? 'pb-64' : ''}`}>
        {/* Panel Principal */}
        {activeTab === 'principal' && (
            <div className="space-y-4">
                <div>
                    <label className="block text-slate-400 text-sm mb-1">Movimiento Manual (pasos)</label>
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleMoverPrincipal(-inputPrincipalManual)}
                            onContextMenu={(e) => e.preventDefault()}
                            disabled={!isConnected || !inputPrincipalManual}
                            className="bg-blue-600 hover:bg-blue-500 active:bg-blue-800 disabled:opacity-50 text-white p-4 rounded-xl flex items-center justify-center gap-1 shadow-lg transition-colors flex-1"
                        >
                            <ArrowLeft size={32} />
                        </button>
                        <input
                            type="number"
                            value={inputPrincipalManual}
                            onChange={(e) => handleUpdateInput('inputPrincipalManual', e.target.value)}
                            onFocus={handleInputFocus}
                            onBlur={() => setIsInputFocused(false)}
                            inputMode="numeric"
                            className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-lg text-white focus:outline-none focus:border-blue-500 text-center"
                        />
                        <button
                            onClick={() => handleMoverPrincipal(inputPrincipalManual)}
                            onContextMenu={(e) => e.preventDefault()}
                            disabled={!isConnected || !inputPrincipalManual}
                            className="bg-blue-600 hover:bg-blue-500 active:bg-blue-800 disabled:opacity-50 text-white p-4 rounded-xl flex items-center justify-center gap-1 shadow-lg transition-colors flex-1"
                        >
                            <ArrowRight size={32} />
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-700/50">
                    <button
                        onClick={() => sendCommand('ENCODER_RESET')}
                        className="bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border border-purple-500/30 py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
                    >
                        <RotateCw size={18} />
                        Reset Encoder
                    </button>
                </div>
            </div>
        )}

        {/* Panel Hilador */}
        {activeTab === 'hilador' && (
          <div className="flex gap-2">
            <div className="flex-1 bg-slate-900/50 p-3 rounded-xl border border-slate-700/50 flex flex-col items-center">
              <div className="text-center mb-3">
                <label className="block text-slate-300 font-medium text-sm">Dentro</label>
              </div>
            <div className="flex gap-3 justify-center">
              <button
                onMouseDown={() => handlePressStart((f) => handleMoverHiladorRelativo(-1 * f, 'anguloDentro'))}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                onTouchStart={e => { e.preventDefault(); handlePressStart((f) => handleMoverHiladorRelativo(-1 * f, 'anguloDentro')); }}
                onTouchEnd={e => { e.preventDefault(); handlePressEnd(); }}
                onContextMenu={e => e.preventDefault()}
                disabled={!isConnected}
                className="bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-white p-3 rounded-full flex items-center justify-center transition-all shadow-lg select-none touch-none"
              >
                <ArrowLeft size={24} />
              </button>
              <button
                onMouseDown={() => handlePressStart((f) => handleMoverHiladorRelativo(1 * f, 'anguloDentro'))}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                onTouchStart={e => { e.preventDefault(); handlePressStart((f) => handleMoverHiladorRelativo(1 * f, 'anguloDentro')); }}
                onTouchEnd={e => { e.preventDefault(); handlePressEnd(); }}
                onContextMenu={e => e.preventDefault()}
                disabled={!isConnected}
                className="bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed text-white p-3 rounded-full flex items-center justify-center transition-all shadow-lg select-none touch-none"
              >
                <ArrowRight size={24} />
              </button>
            </div>
            </div>
            <div className="flex-1 bg-slate-900/50 p-3 rounded-xl border border-slate-700/50 flex flex-col items-center">
              <div className="text-center mb-3">
                <label className="block text-slate-300 font-medium text-sm">Fuera</label>
              </div>
            <div className="flex gap-3 justify-center">
              <button
                onMouseDown={() => handlePressStart((f) => handleMoverHiladorRelativo(-1 * f, 'anguloFuera'))}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                onTouchStart={e => { e.preventDefault(); handlePressStart((f) => handleMoverHiladorRelativo(-1 * f, 'anguloFuera')); }}
                onTouchEnd={e => { e.preventDefault(); handlePressEnd(); }}
                onContextMenu={e => e.preventDefault()}
                disabled={!isConnected}
                className="bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-white p-3 rounded-full flex items-center justify-center transition-all shadow-lg select-none touch-none"
              >
                <ArrowLeft size={24} />
              </button>
              <button
                onMouseDown={() => handlePressStart((f) => handleMoverHiladorRelativo(1 * f, 'anguloFuera'))}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                onTouchStart={e => { e.preventDefault(); handlePressStart((f) => handleMoverHiladorRelativo(1 * f, 'anguloFuera')); }}
                onTouchEnd={e => { e.preventDefault(); handlePressEnd(); }}
                onContextMenu={e => e.preventDefault()}
                disabled={!isConnected}
                className="bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed text-white p-3 rounded-full flex items-center justify-center transition-all shadow-lg select-none touch-none"
              >
                <ArrowRight size={24} />
              </button>
            </div>
            </div>
          </div>
        )}

        {/* Panel Elevador */}
        {activeTab === 'elevador' && (
          <div className="space-y-4">
            <div>
              <label className="block text-slate-400 text-sm mb-1">Movimiento Manual (mm)</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleMoverElevador(-inputElevadorManual)}
                  disabled={!isConnected || !inputElevadorManual}
                  className="bg-red-600 hover:bg-red-500 active:bg-red-800 disabled:opacity-50 text-white p-4 rounded-xl flex items-center justify-center gap-1 shadow-lg transition-colors flex-1"
                >
                  <ChevronDown size={32} />
                </button>
                <input
                  type="number"
                  value={inputElevadorManual}
                  onChange={(e) => handleUpdateInput('inputElevadorManual', e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={() => setIsInputFocused(false)}
                  inputMode="decimal"
                  className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-lg text-white focus:outline-none focus:border-blue-500 text-center"
                />
                <button
                  onClick={() => handleMoverElevador(inputElevadorManual)}
                  disabled={!isConnected || !inputElevadorManual}
                  className="bg-green-600 hover:bg-green-500 active:bg-green-800 disabled:opacity-50 text-white p-4 rounded-xl flex items-center justify-center gap-1 shadow-lg transition-colors flex-1"
                >
                  <ChevronUp size={32} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
                {[-5, -1, 1, 5].map(v => (
                    <button
                        key={v}
                        onClick={() => handleMoverElevador(v)}
                        disabled={!isConnected}
                        className={`${v < 0 ? 'bg-red-600' : 'bg-green-600'} hover:opacity-80 active:scale-95 disabled:opacity-50 text-white p-4 rounded-xl font-mono text-xl font-bold shadow-lg transition-all`}
                    >
                        {v > 0 ? `+${v}` : v}
                    </button>
                ))}
            </div>
          </div>
        )}

        {/* Panel Auxiliar (posicionador de la pistola clavadora en el eje X) */}
        {activeTab === 'auxiliar' && (
          <div className="space-y-4">
            {/* Posición estimada + acciones de cero */}
            <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/50 flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-xs">Posición estimada</div>
                <div className="text-2xl font-mono font-bold text-cyan-400">
                  {posAuxiliarCm > 0 ? '+' : ''}{posAuxiliarCm} <span className="text-sm text-slate-500">cm</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAuxiliarSetCero}
                  disabled={!isConnected}
                  className="bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border border-purple-500/30 py-2 px-3 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                >
                  <Crosshair size={16} />
                  Fijar cero
                </button>
                <button
                  onClick={handleAuxiliarIrACero}
                  disabled={!isConnected}
                  className="bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 border border-cyan-500/30 py-2 px-3 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                >
                  <RotateCw size={16} />
                  Ir a cero
                </button>
              </div>
            </div>

            {/* Movimiento manual en cm */}
            <div>
              <label className="block text-slate-400 text-sm mb-1">Movimiento Manual (cm)</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleMoverAuxiliarCm(-parseFloat(inputAuxiliarManual))}
                  onContextMenu={(e) => e.preventDefault()}
                  disabled={!isConnected || !parseFloat(inputAuxiliarManual)}
                  className="bg-blue-600 hover:bg-blue-500 active:bg-blue-800 disabled:opacity-50 text-white p-4 rounded-xl flex items-center justify-center gap-1 shadow-lg transition-colors flex-1"
                >
                  <ArrowLeft size={32} />
                </button>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={inputAuxiliarManual}
                  onChange={(e) => handleUpdateInput('inputAuxiliarManual', e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={() => setIsInputFocused(false)}
                  inputMode="decimal"
                  className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-lg text-white focus:outline-none focus:border-blue-500 text-center"
                />
                <button
                  onClick={() => handleMoverAuxiliarCm(parseFloat(inputAuxiliarManual))}
                  onContextMenu={(e) => e.preventDefault()}
                  disabled={!isConnected || !parseFloat(inputAuxiliarManual)}
                  className="bg-blue-600 hover:bg-blue-500 active:bg-blue-800 disabled:opacity-50 text-white p-4 rounded-xl flex items-center justify-center gap-1 shadow-lg transition-colors flex-1"
                >
                  <ArrowRight size={32} />
                </button>
              </div>
            </div>

            {/* Botones rápidos en cm */}
            <div className="grid grid-cols-4 gap-3">
              {[-1, -0.1, 0.1, 1].map(v => (
                <button
                  key={v}
                  onClick={() => handleMoverAuxiliarCm(v)}
                  disabled={!isConnected}
                  className={`${v < 0 ? 'bg-red-600' : 'bg-green-600'} hover:opacity-80 active:scale-95 disabled:opacity-50 text-white p-3 rounded-xl font-mono text-lg font-bold shadow-lg transition-all`}
                >
                  {v > 0 ? `+${v}` : v}
                </button>
              ))}
            </div>

            {/* Calibración pulsos por cm */}
            <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-300 font-medium text-sm">
                  <Ruler size={16} className="text-yellow-400" />
                  Calibración (pulsos por cm)
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.01"
                    value={pulsosPorCmAuxiliar}
                    onChange={(e) => handleUpdateInput('pulsosPorCmAuxiliar', parseFloat(e.target.value) || 0)}
                    onFocus={handleInputFocus}
                    onBlur={() => setIsInputFocused(false)}
                    inputMode="decimal"
                    className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-2 py-1 text-sm text-yellow-400 font-mono focus:outline-none focus:border-yellow-500 text-center"
                  />
                  <span className="text-slate-500 text-xs">p/cm</span>
                </div>
              </div>

              {/* Paso 1: mover pulsos de prueba */}
              <div>
                <label className="block text-slate-400 text-xs mb-1">1. Mueve el motor en pulsos y mide el desplazamiento con una regla</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleMoverAuxiliarPulsos(-parseInt(calibPulsosInput))}
                    onContextMenu={(e) => e.preventDefault()}
                    disabled={!isConnected || !parseInt(calibPulsosInput)}
                    className="bg-slate-700 hover:bg-slate-600 active:bg-slate-800 disabled:opacity-50 text-white p-2.5 rounded-lg flex items-center justify-center shadow transition-colors flex-1"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <input
                    type="number"
                    value={calibPulsosInput}
                    onChange={(e) => setCalibPulsosInput(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={() => setIsInputFocused(false)}
                    inputMode="numeric"
                    className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white focus:outline-none focus:border-blue-500 text-center"
                  />
                  <button
                    onClick={() => handleMoverAuxiliarPulsos(parseInt(calibPulsosInput))}
                    onContextMenu={(e) => e.preventDefault()}
                    disabled={!isConnected || !parseInt(calibPulsosInput)}
                    className="bg-slate-700 hover:bg-slate-600 active:bg-slate-800 disabled:opacity-50 text-white p-2.5 rounded-lg flex items-center justify-center shadow transition-colors flex-1"
                  >
                    <ArrowRight size={20} />
                  </button>
                </div>
                <div className="text-center text-xs text-slate-400 mt-1.5">
                  Pulsos acumulados: <span className={`font-mono font-bold ${calibPulsosAcumulados !== 0 ? 'text-yellow-400' : 'text-slate-500'}`}>{calibPulsosAcumulados}</span>
                </div>
              </div>

              {/* Paso 2: cm medidos */}
              <div>
                <label className="block text-slate-400 text-xs mb-1">2. ¿Cuántos cm se movió la pistola?</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={calibCmMedidos}
                  onChange={(e) => setCalibCmMedidos(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={() => setIsInputFocused(false)}
                  inputMode="decimal"
                  placeholder="ej. 12.5"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-yellow-500 text-center"
                />
              </div>

              {/* Paso 3: calcular y guardar */}
              <div className="flex gap-2">
                <button
                  onClick={handleCalcularCalibracion}
                  disabled={!parseFloat(calibCmMedidos) || calibPulsosAcumulados === 0}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-500 active:bg-yellow-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white py-2.5 rounded-lg font-medium text-sm shadow transition-colors"
                >
                  3. Calcular y guardar
                </button>
                <button
                  onClick={handleReiniciarCalibracion}
                  className="bg-slate-700 hover:bg-slate-600 text-slate-300 py-2.5 px-3 rounded-lg text-sm shadow transition-colors flex items-center gap-1.5"
                >
                  <RefreshCw size={14} />
                  Reiniciar
                </button>
              </div>

              {calibResultado !== null && (
                <div className="bg-green-900/30 border border-green-700/40 rounded-lg px-3 py-2 text-center text-sm text-green-400">
                  Guardado: <span className="font-mono font-bold">{calibResultado}</span> pulsos/cm
                </div>
              )}
            </div>

            {/* Toggle inversión de dirección */}
            <div className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between border border-slate-700/50">
              <div>
                <span className="text-sm font-medium text-slate-300">Invertir dirección</span>
                <p className="text-[10px] text-slate-500 mt-0.5">Actívalo si el motor se mueve al revés</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={invertirAuxiliar}
                  onChange={(e) => handleInvertirAuxiliar(e.target.checked)}
                  disabled={!isConnected}
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
              </label>
            </div>
          </div>
        )}

        {/* Panel Encoder */}
        {activeTab === 'encoder' && (() => {
          const umbral = motorConfig?.umbralPulsosEncoder ?? 50;
          const minOk = encoderHistory.filter(e => e.tipo === 'ok').reduce((m, e) => Math.min(m, e.pulsos), Infinity);
          const tipoColor = { ok: 'text-green-400', sospechoso: 'text-yellow-400', roto: 'text-red-400' };
          const tipoBg = { ok: 'bg-slate-900/40 border-slate-700/30', sospechoso: 'bg-yellow-900/20 border-yellow-700/30', roto: 'bg-red-900/30 border-red-700/40' };
          const tipoLabel = { ok: 'OK', sospechoso: 'SOSPECHOSO', roto: 'HILO ROTO' };
          return (
            <div className="space-y-3">
              {/* Valor en vivo */}
              <div className="bg-slate-900/60 rounded-xl p-4 border border-slate-700/50">
                <div className="text-slate-400 text-xs mb-1">Pulsos encoder (último ciclo)</div>
                <div className={`text-4xl font-mono font-bold ${encoderLiveValue === null ? 'text-slate-500' : encoderLiveValue < umbral ? 'text-red-400' : 'text-green-400'}`}>
                  {encoderLiveValue === null ? '—' : encoderLiveValue}
                </div>
                <div className="text-slate-500 text-xs mt-1">Umbral: {umbral}</div>
              </div>

              {/* Stats sesión */}
              {encoderHistory.length > 0 && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-900/40 rounded-lg p-2">
                    <div className="text-slate-400 text-xs">Ciclos</div>
                    <div className="text-white font-mono font-bold">{encoderHistory.length}</div>
                  </div>
                  <div className="bg-slate-900/40 rounded-lg p-2">
                    <div className="text-slate-400 text-xs">Mínimo OK</div>
                    <div className={`font-mono font-bold ${minOk === Infinity ? 'text-slate-500' : minOk < umbral ? 'text-yellow-400' : 'text-green-400'}`}>
                      {minOk === Infinity ? '—' : minOk}
                    </div>
                  </div>
                  <div className="bg-slate-900/40 rounded-lg p-2">
                    <div className="text-slate-400 text-xs">Fallos</div>
                    <div className={`font-mono font-bold ${encoderHistory.filter(e => e.tipo === 'roto').length > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                      {encoderHistory.filter(e => e.tipo === 'roto').length}
                    </div>
                  </div>
                </div>
              )}

              {/* Historial */}
              <div>
                <div className="text-slate-400 text-xs mb-2 flex items-center justify-between">
                  <span>Historial de ciclos ({encoderHistory.length}/50)</span>
                  {encoderHistory.length === 0 && <span className="text-slate-600">sin datos aún</span>}
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {[...encoderHistory].reverse().map((entry, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-sm border ${tipoBg[entry.tipo] ?? 'bg-slate-900/40 border-slate-700/30'}`}
                    >
                      <span className={`font-mono font-bold text-base ${tipoColor[entry.tipo] ?? 'text-slate-400'}`}>
                        {entry.pulsos}{entry.acumulado !== undefined ? <span className="text-xs ml-1 opacity-70">(acum {entry.acumulado})</span> : null}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${entry.tipo === 'roto' ? 'bg-red-700/50 text-red-300' : entry.tipo === 'sospechoso' ? 'bg-yellow-700/40 text-yellow-300' : 'bg-green-900/40 text-green-400'}`}>
                        {tipoLabel[entry.tipo] ?? entry.tipo}
                      </span>
                      <span className="text-slate-500 text-xs">
                        {new Date(entry.ts).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Panel Disparador */}
        {activeTab === 'disparador' && (
          <div className="space-y-4">
            <div className="bg-slate-900/50 rounded-lg p-4 flex items-center justify-between ">
              <button
                onClick={handleRecargar}
                className="bg-cyan-700 hover:bg-cyan-600 active:bg-cyan-800 text-white py-4 rounded-lg px-3 font-medium flex items-center justify-center gap-2 text-sm shadow-lg transition-colors"
              >
                <RefreshCw size={18} />
                <span>Recargar (90)</span>
              </button>
              <div className="text-center">
                <div className="text-slate-400 text-sm mb-1">Clavos</div>
                <div className={`text-4xl font-bold font-mono ${clavosRestantes === 0 ? 'text-red-500' : 'text-green-400'}`}>
                  {clavosRestantes}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setClavosRestantes(Math.max(0, clavosRestantes - 1))} className="p-3 rounded-full bg-red-600 hover:bg-red-400 text-white shadow transition-all"><ArrowLeft size={20} /></button>
                <button onClick={() => setClavosRestantes(Math.min(90, clavosRestantes + 1))} className="p-3 rounded-full bg-green-500 hover:bg-green-400 text-white shadow transition-all"><ArrowRight size={20} /></button>
              </div>
            </div>
            <button
              onClick={handleDisparar}
              disabled={!isConnected || clavosRestantes === 0}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold text-xl flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all"
            >
              <Target size={24} />
              DISPARAR
            </button>

            {/* Toggle inversión del relay */}
            <div className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between border border-slate-700/50">
              <div>
                <span className="text-sm font-medium text-slate-300">Relevador NC</span>
                <p className="text-[10px] text-slate-500 mt-0.5">Actívalo si usas un relay NC</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={invertirRele}
                  onChange={(e) => handleInvertirRele(e.target.checked)}
                  disabled={!isConnected}
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualToolControl;

